import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './';

export interface DialogProps {
  /** ダイアログが開いているか */
  open: boolean;
  /** 閉じる時のコールバック */
  onClose: () => void;
  /** ダイアログタイトル */
  title?: string;
  /** ダイアログタイプ */
  type?: 'modal' | 'alert';
  /** 子要素（コンテンツ） */
  children?: ReactNode;
  /** フッター要素 */
  footer?: ReactNode;
  /** 閉じるボタンを表示するか（modalタイプのみ） */
  showCloseButton?: boolean;
  /** 追加のクラス名 */
  className?: string;
}

/**
 * ダイアログコンポーネント
 */
export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  type = 'modal',
  children,
  footer,
  showCloseButton = true,
  className = '',
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // ダイアログの開閉を制御
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }
  }, [open]);

  // ESCキーでの閉じる処理
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  // バックドロップクリックで閉じる（modalタイプのみ）
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (type === 'modal' && e.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      data-type={type}
      className={className}
      onClick={handleBackdropClick}
    >
      {title && (
        <div className="aip-dialog__header">
          <h2 className="aip-dialog__title">{title}</h2>
          {type === 'modal' && showCloseButton && (
            <Button
              buttonType="icon-only"
              size="md"
              onClick={onClose}
              aria-label="閉じる"
            >
              <span className="material-symbols-outlined">close</span>
            </Button>
          )}
        </div>
      )}
      {children && <div className="aip-dialog__content">{children}</div>}
      {footer && <div className="aip-dialog__footer">{footer}</div>}
    </dialog>
  );
};

export interface ConfirmDialogProps {
  /** ダイアログが開いているか */
  open: boolean;
  /** 閉じる時のコールバック */
  onClose: () => void;
  /** 確認時のコールバック */
  onConfirm: () => void;
  /** ダイアログタイトル */
  title: string;
  /** メッセージ */
  message: string | ReactNode;
  /** 確認ボタンのラベル */
  confirmLabel?: string;
  /** キャンセルボタンのラベル */
  cancelLabel?: string;
  /** 確認ボタンのタイプ（危険なアクションの場合は alert） */
  confirmType?: 'default' | 'alert';
  /** 読み込み中かどうか */
  loading?: boolean;
}

/**
 * 確認ダイアログコンポーネント
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = '確認',
  cancelLabel = 'キャンセル',
  confirmType = 'default',
  loading = false,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      type="alert"
      footer={
        <>
          <Button
            buttonType="outline"
            size="md"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            buttonType="solid-fill"
            size="md"
            state={confirmType === 'alert' ? 'alert' : undefined}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? '処理中...' : confirmLabel}
          </Button>
        </>
      }
    >
      <p>{message}</p>
    </Dialog>
  );
};

export default Dialog;




